import { Previewer, Handler, registerHandlers, registeredHandlers } from 'pagedjs';
import buildPagedReportCss from './pagedReportCss';

// The report title block lifted out of the paginated flow by paginateReport (see
// there for why). The handler re-inserts a clone into every page's top margin box.
// paged.js instantiates the handler internally (registered once, below), so this is
// the handoff channel. Keyed by the per-call content node (== chunker.source, the
// exact node paginateReport passes into previewer.preview()) rather than a single
// module-scoped variable, so two paginateReport calls in flight at once - two
// windows, or a rapid re-paginate - each keep their own report's header instead of
// racing to overwrite one shared slot.
const pendingReportHeaders = new WeakMap();

// Per-run page-emission channel for progressive rendering. paginateReport
// registers { cb, count, clones } keyed by its per-call content node (the exact
// node the handler sees as chunker.source - same keying as pendingReportHeaders,
// so two concurrent paginations can't cross-talk). The handler emits an inert
// clone of each page as soon as afterPageLayout finishes with it; pages are laid
// out strictly in document order, so `count` is the page index.
const pendingPageCallbacks = new WeakMap();

// Test seam mirroring _setPendingReportHeader: register a page callback for a
// given content node. Returns the internal entry (count/clones accumulate).
export function _registerPageCallback(contentKey, cb) {
  const entry = { cb, count: 0, clones: [] };
  pendingPageCallbacks.set(contentKey, entry);
  return entry;
}

// Literal page-number stamp into a page's bottom-right margin box (idempotent).
// Literal text, not CSS counter(page): the viewer displays detached page CLONES
// and print reveals the renderTarget originals; a counter depends on document
// order and mounting of whatever subset is attached, stamped text survives both.
const stampPageNumber = (pageEl, i) => {
  const box = pageEl.querySelector('.pagedjs_margin-bottom-right .pagedjs_margin-content');
  if (box && !box.querySelector('.report-page-number')) {
    const num = document.createElement('div');
    num.className = 'report-page-number';
    num.textContent = String(i + 1);
    box.innerHTML = '';
    box.appendChild(num);
  }
};

// Per-page report headers, so every page reads on its own like the printed Banyan
// PDFs. paged.js `afterPageLayout(pageElement, page, breakToken, chunker)` fires once
// per rendered page with the finished page DOM. This handler does two things per page:
//
//   1. Report title block -> the page's `@top-center` margin box (declared + sized via
//      @page margin-top in pagedReportCss.js). paged.js's own `position: running()`
//      emits a blank first page when the running element is the flow's first child, and
//      even a plain hidden first child does the same, so paginateReport REMOVES the
//      header from the paginated content entirely and stashes it in pendingReportHeaders,
//      keyed by that call's content node; we clone that into the margin. (chunker.source
//      fallback keeps the unit tests and any header left in-flow working.)
//   2. Column headers: paged.js 0.4.3 does NOT repeat a table's <thead> when the table
//      splits across pages (dom.js `rebuildAncestors` shallow-clones ancestors and
//      drops the header), so column headers would appear only on a table's first page.
//      We re-clone the ORIGINAL <thead> onto every split-continuation table.
//
// Both apply to EVERY report because every report paginates through here.
export class RepeatReportHeaders extends Handler {
  // Column headers, injected DURING layout (not afterPageLayout). paged.js fires
  // renderNode(clone, node, layout) inside Layout.append (chunker/layout.js:317,
  // via this.hooks.renderNode.triggerSync) right after rebuildAncestors has
  // materialized the continuation table shell on the page, but BEFORE overflow
  // detection sizes the page. Cloning the original <thead> back onto the split
  // table here means paged.js counts the ~40px header height while choosing which
  // rows fit, so rows that no longer fit are pushed to the next page. Doing this
  // in afterPageLayout instead (the old-only path, kept below as a belt) shifts
  // every already-placed row down by the header height AFTER layout - and Gecko,
  // which packs pages with near-zero slack, then clips the bottom 1-3 rows past
  // the fixed page box (content loss in print). The base ctor stored the chunker,
  // so this.chunker.source is the original (pre-chunk) content node.
  renderNode(clone) {
    try {
      if (!clone || clone.nodeType !== 1) return;
      const tag = clone.tagName;
      if (tag !== 'TR' && tag !== 'TBODY') return;
      const table = clone.closest('table[data-split-from]');
      if (!table || table.querySelector('thead')) return; // not a split table, or already headed
      const ref = (table.dataset && (table.dataset.splitFrom || table.dataset.ref)) || '';
      const source = this.chunker && this.chunker.source;
      if (!ref || !source) return;
      const srcThead = source.querySelector(`[data-ref="${ref}"] > thead`);
      if (srcThead) table.insertBefore(srcThead.cloneNode(true), table.firstChild);
    } catch (_) { /* pagination must never throw from a hook */ }
  }

  // Whole-row relocation safety net. paged.js fires
  // onOverflow(overflow, rendered, bounds, layout) in Layout.findBreakToken
  // (chunker/layout.js:484); a non-undefined return REPLACES the overflow Range,
  // which then drives the break token and removeOverflow. We widen the overflow
  // backward to before the FIRST row that overshoots the page box, so a row that
  // a rounding overshoot (the residual +7px Gecko case) or any future mid-row
  // token would otherwise clip is relocated whole to the next page. This is
  // provably a no-op when the break is already clean (findOverflow already broke
  // before the straddling row), and it never narrows an existing overflow.
  onOverflow(overflow, rendered, bounds) {
    try {
      if (!overflow || !rendered || !bounds) return undefined;
      const rows = rendered.querySelectorAll('tr');
      let straddler = null;
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i].getBoundingClientRect();
        // Overshoots the box bottom AND is not the page's first content (a row
        // taller than the page must stay, else it relocates forever).
        if (r.bottom > bounds.bottom + 1 && r.top > bounds.top + 1) {
          straddler = rows[i];
          break;
        }
      }
      if (!straddler) return undefined;
      // Relocate the row's parent <tbody> ONLY when it is declared atomic
      // (break-inside: avoid — the FI row-keep / section-keep wrappers), so the
      // keep-together unit moves whole. A PLAIN shared tbody holding every row
      // (Security Cross Reference, the billing daily tables) must NOT be the
      // target: widening to it relocates the entire body again and again —
      // Gecko renders a thead-only page, then hits paged.js's repeated-break
      // guard and silently TRUNCATES the remaining rows (29/41 lost in the
      // xref repro; days 25-31 missing from printed billing PDFs).
      let target = straddler;
      const parent = straddler.parentElement;
      if (parent && parent.tagName === 'TBODY') {
        const bi = window.getComputedStyle(parent).breakInside || '';
        if (bi.startsWith('avoid')) target = parent;
      }
      const targetStart = document.createRange();
      targetStart.setStartBefore(target);
      // Only WIDEN backward: act only when the overflow currently begins AFTER the
      // target's start (compareBoundaryPoints returns 1); otherwise leave it.
      if (overflow.compareBoundaryPoints(Range.START_TO_START, targetStart) > 0) {
        overflow.setStartBefore(target);
        return overflow;
      }
      return undefined;
    } catch (_) {
      return undefined; // pagination must never throw from a hook
    }
  }

  afterPageLayout(pageElement, page, breakToken, chunker) {
    const source = chunker && chunker.source;
    if (!pageElement) return;

    // Disable this page's live ResizeObserver BEFORE we mutate it. paged.js attaches
    // a per-page RO (Page.addResizeObserver) that re-flows on size change; injecting
    // the margin header / theads below resizes the page and would fire it, and its
    // reflow (checkUnderflowAfterResize -> findEndToken -> findElement) throws on the
    // now-mutated DOM — an uncatchable error in the RO's rAF callback. Detaching the
    // listeners here (belt; paginate.js also does it post-preview) prevents that.
    if (page && typeof page.removeListeners === 'function') page.removeListeners();

    // 1. Title block -> top-center margin box (present on every page).
    const marginBox = pageElement.querySelector('.pagedjs_margin-top-center .pagedjs_margin-content');
    const stashedHeader = source && pendingReportHeaders.get(source);
    const srcHeader = stashedHeader || (source && source.querySelector('.report-running-header'));
    if (marginBox && srcHeader && !marginBox.querySelector('.report-running-header')) {
      marginBox.innerHTML = '';
      marginBox.appendChild(srcHeader.cloneNode(true));
    }

    // 2. Column headers -> every split-continuation table.
    if (source) {
      pageElement.querySelectorAll('table[data-split-from]').forEach((table) => {
        if (table.querySelector('thead')) return; // already carries a header
        const ref = (table.dataset && (table.dataset.splitFrom || table.dataset.ref)) || '';
        const srcThead = ref && source.querySelector(`[data-ref="${ref}"] > thead`);
        if (srcThead) table.insertBefore(srcThead.cloneNode(true), table.firstChild);
      });
    }

    // 3. Progressive page emission. Runs LAST so the emitted clone already
    // carries this page's margin header + re-injected theads. The page is final
    // here: paged.js's forward-only flow never revisits a laid-out page, and its
    // live ResizeObserver was detached above. Stamp the page number on the LIVE
    // page first (index = emission order = document order) so print originals
    // and the clone agree, then hand an inert clone to the registered viewer.
    try {
      const entry = source && pendingPageCallbacks.get(source);
      if (entry) {
        const i = entry.count;
        entry.count += 1;
        stampPageNumber(pageElement, i);
        const clone = pageElement.cloneNode(true);
        entry.clones.push(clone);
        entry.cb(clone, i);
      }
    } catch (_) { /* pagination must never throw from a hook */ }
  }
}

// Test/handoff helper: set the report header the handler injects into each page margin
// for a given content node (the same node the handler will see as chunker.source).
export function _setPendingReportHeader(node, contentKey) { pendingReportHeaders.set(contentKey, node); }

// Register ONCE. `registeredHandlers` is a module-global paged.js array that every
// `new Previewer().preview()` instantiates; registering per-call (or twice under
// HMR) would clone the headers multiple times per page.
if (!registeredHandlers.includes(RepeatReportHeaders)) {
  registerHandlers(RepeatReportHeaders);
}

// Thin wrapper over paged.js: paginate a report body node into landscape pages.
//
// paged.js's Previewer.preview(content, stylesheets, renderTo):
//   - content: DOM node / HTML. It MOVES nodes while chunking, so we pass a CLONE
//     and leave the caller's source node intact (it doubles as the xlsx-export DOM).
//   - stylesheets: an inline { name: cssText } map is fed straight to the Polisher
//     (no fetch), so our @page/running-header rules apply WITHOUT touching the
//     document's global stylesheets. The app's Tailwind CSS still styles the pages
//     because they render into the live document.
//   - renderTo: the target element; paged.js appends a `.pagedjs_pages` wrapper of
//     `.pagedjs_page` elements to it.
//
// Returns an ordered array of INERT `.pagedjs_page` CLONES (see the clone step
// below). The live originals stay in `renderTo` (offscreen for the viewer, or the
// print root for printing) — untouched, so their observers never fire.
//
// options.onPage(clone, index): progressive rendering. Called once per page, in
// strict page order, the moment that page's layout is FINAL (after its margin
// header + theads are injected and its live ResizeObserver is detached), with an
// inert stamped clone. When every page emitted, the resolved array contains the
// SAME nodes, so callers can mount progressively and keep them on completion.
//
// options.append: when true, do NOT clear renderTarget first, so successive calls
// accumulate their `.pagedjs_pages` wrappers back-to-back. Used to print a group
// report member-by-member (each member paginated separately so it gets its OWN
// per-page running header) into one continuous #report-print-root. Page numbers
// stamp by absolute position in the accumulating target (stampPageNumber skips
// already-stamped boxes), so numbering runs continuously across members.
export async function paginateReport(sourceNode, renderTarget, { print = false, onPage, append = false } = {}) {
  if (!sourceNode || !renderTarget) return [];
  if (!append) renderTarget.innerHTML = '';
  const content = sourceNode.cloneNode(true);
  // Progressive rendering: register the per-page callback under this call's
  // content node BEFORE preview starts, so the handler emits an inert clone of
  // each page (stamped, headered) the moment it is laid out. `onPage(clone, i)`
  // fires strictly in page order.
  const pageEntry = onPage ? _registerPageCallback(content, onPage) : null;
  // CRITICAL: the live source node is a positioning shell (`absolute -left-[99999px]
  // w-[1056px] bg-white`) that parks the report body offscreen. paged.js renders
  // page 1 from the flow root's CHILDREN, but on every CONTINUATION page it
  // re-materializes the flow root itself as the content's ancestor (data-split-from)
  // — so any class/style left on the clone root drags pages 2+ 99999px offscreen.
  // Those pages still lay out and hit-test (elementFromPoint finds an unaffected
  // ancestor whose textContent includes the offscreen text), but they PAINT nothing:
  // white sheets. Strip the root's presentation entirely; page 1 never used it.
  content.removeAttribute('class');
  content.removeAttribute('style');
  // CRITICAL (two birds, one parallel walk - MUST run before the header lift /
  // data-export-skip removals below, which desync the clone's element list
  // from the source's):
  //   1. Neutralize on-screen scroll containers: paged.js CANNOT break content
  //      inside an `overflow != visible` box, so a page-tall table wrapped in
  //      one (e.g. FI Holdings by Maturity) silently fails to paginate.
  //   2. Stamp each element's REAL computed display inline. The neutralization
  //      gives every node a [style] attribute, and paged.js's UndisplayedFilter
  //      marks any [style] element lacking an explicit inline `display` as
  //      data-undisplayed - poisoning the whole tree: displayed-neighbor walks
  //      skip everything and forced break-before/after rules NEVER fire
  //      (observed: billing's per-client break-before:page dead). The stamp
  //      must be the element's OWN computed value read from its attached
  //      source twin - NOT `revert` (rolls back past Tailwind to UA defaults:
  //      flex/grid/hidden collapse to block) and NOT a guess. Genuinely hidden
  //      elements stamp display:none and are then CORRECTLY marked undisplayed.
  //      The clone is detached (computed style unreadable), so read the live
  //      sourceNode's identical-order element list; in jsdom both lists still
  //      align and getComputedStyle degrades to defaults, which is harmless.
  const sourceEls = sourceNode.querySelectorAll('*');
  content.querySelectorAll('*').forEach((el, i) => {
    if (!el.style) return;
    el.style.overflow = 'visible';
    el.style.maxHeight = 'none';
    const twin = sourceEls[i];
    if (twin && twin.nodeType === 1) {
      const disp = window.getComputedStyle(twin).display;
      if (disp) el.style.display = disp;
    }
  });
  // Measure the ORIGINAL (un-cloned, live) header before it's lifted out of the
  // clone below, so the paged @page top margin can be sized to the real header
  // instead of a fixed guess. The source div is rendered offscreen at 1056px
  // width, so offsetHeight is real there; in jsdom it's always 0, which safely
  // falls back to the old fixed 1.85in band (see buildPagedReportCss).
  const srcHeader = sourceNode.querySelector('.report-running-header');
  const headerPx = srcHeader ? srcHeader.offsetHeight : 0;
  // Lift the report title block OUT of the paginated flow and stash it (keyed by
  // this call's own `content` node) for the RepeatReportHeaders handler, which
  // re-inserts it into every page's top margin box. It must not stay in the flow:
  // paged.js emits an empty first page when the first flow child is hidden
  // (display:none) or a `position: running()` element, and the header would
  // otherwise render inline on page 1 only. Removing it sidesteps that entirely -
  // the paginated content simply has no header node.
  const headerNode = content.querySelector('.report-running-header');
  if (headerNode) {
    pendingReportHeaders.set(content, headerNode.cloneNode(true));
    headerNode.remove();
  }
  // Drop interactive chrome (member tabs, notices, yield selector) — it isn't part
  // of the paginated document, and CSS-hiding it via the passed stylesheet is
  // unreliable in the Previewer path, so remove the nodes outright. (Runs AFTER
  // the parallel style walk above, which needs source/clone index parity.)
  content.querySelectorAll('[data-export-skip]').forEach((el) => el.remove());
  // Screen-only controls (e.g. the gains Expand All button) stay in the viewer
  // pages - they are the click surface the delegated handler resolves - but are
  // stripped from the PRINT pagination so they never land on paper.
  if (print) content.querySelectorAll('[data-print-skip]').forEach((el) => el.remove());
  // (The overflow neutralization that used to live here moved into the parallel
  // source/clone walk above, which additionally stamps each element's real
  // computed display - see that comment for why both must happen together.)
  // CRITICAL: keep table rows whole across page breaks. paged.js 0.4.3 has TWO
  // break paths, and only the element path (layout.js findOverflow ~559) reads
  // break-inside:avoid from computed CSS. The TEXT path (~640) - taken when a
  // multi-line cell (bond Description) straddles the page edge - is guarded
  // solely by breakInsideAvoidParentNode(), which checks the data-break-inside
  // ATTRIBUTE... which nothing in paged.js ever writes (its Breaks CSS handler
  // stamps break-before/after/page only). So without this stamp that guard is
  // permanently false and rows get sliced mid-Description in any engine
  // (observed in Safari and Gecko; geometry-dependent, hence intermittent).
  // The attribute survives into the rendered pages via cloning, trips the
  // guard, and the element path then relocates the whole row. The CSS rules in
  // pagedReportCss.js still cover the element path; this covers the text path.
  content.querySelectorAll('tr, td').forEach((el) => el.setAttribute('data-break-inside', 'avoid'));
  const previewer = new Previewer();
  // +8px breathing room: the margin band centers the header via flex, and
  // zeroing its margins (pagedReportCss.js) means offsetHeight slightly
  // overestimates its rendered box, so the centered leftover is a few px - which
  // is the intended small gap, not the old dead band.
  const pagedReportCss = buildPagedReportCss(headerPx > 0 ? headerPx + 8 : 0);
  const flow = await previewer.preview(content, [{ 'paged-report.css': pagedReportCss }], renderTarget);
  // paged.js keeps a LIVE ResizeObserver on every rendered page (Page.addResizeObserver ->
  // checkUnderflow/OverflowAfterResize) to re-flow content if a page's box changes after render.
  // Our viewer MOVES pages into scaled display slots — detaching/re-appending a page transiently
  // drops its measured height, which fires that observer; its reflow (findEndToken -> findElement)
  // then walks the now-detached source and throws `Cannot read properties of null (reading
  // 'getAttribute')`, corrupting the page so it renders BLANK (only page 1, rendered before any
  // move, survived). Belt: disable each live page's listeners.
  try {
    (flow && flow.pages ? flow.pages : []).forEach((p) => {
      if (p && typeof p.removeListeners === 'function') p.removeListeners();
    });
  } catch (e) {
    console.warn('paginate: could not disable paged.js page listeners', e); // eslint-disable-line no-console
  }
  // Small page number into every page's bottom-right margin box (the box exists
  // because @bottom-right declares content in pagedReportCss.js, same trick as
  // the @top-center title band). Stamped as LITERAL text after layout - see
  // stampPageNumber for why not CSS counter(page). When a progressive run
  // already stamped a page in afterPageLayout this is an idempotent no-op
  // (stampPageNumber skips stamped boxes); it remains the only stamp for the
  // print path and any page a hook miss left unstamped. Runs after the pages'
  // ResizeObservers are disabled (above), so the margin-box mutation cannot
  // trigger a reflow.
  renderTarget.querySelectorAll('.pagedjs_page').forEach((pageEl, i) => stampPageNumber(pageEl, i));
  // The Polisher injects our paged stylesheet (including its @page size/margin
  // rules) into <head> and leaves it there after pagination. At PRINT time that
  // @page sits later in the cascade than App.css's `@page { margin: 0 }` (which
  // maps each pre-rendered, already-margined .pagedjs_page sheet 1:1 onto
  // paper), so the browser would ADD paged.js's 0.5in/1.6in sheet margins on
  // top - shrinking the printable area, slicing rows at page boundaries and
  // pushing trailing content (cash section, grand total) off the printout.
  // The rendered pages carry their geometry as classes/inline styles, so the
  // @page rules are dead weight once layout is done - delete them, keep the rest.
  try {
    document.head.querySelectorAll('style[data-pagedjs-inserted-styles]').forEach((styleEl) => {
      const sheet = styleEl.sheet;
      if (!sheet || !sheet.cssRules) return;
      for (let i = sheet.cssRules.length - 1; i >= 0; i -= 1) {
        // CSSRule.PAGE_RULE === 6; constructor check covers engines where the
        // numeric constant is missing.
        const rule = sheet.cssRules[i];
        if (rule.type === 6 || (rule.constructor && rule.constructor.name === 'CSSPageRule')) {
          sheet.deleteRule(i);
        }
      }
    });
  } catch (e) {
    console.warn('paginate: could not strip leaked @page print rules', e); // eslint-disable-line no-console
  }
  // Suspenders: hand the viewer INERT CLONES of the pages. cloneNode copies the markup +
  // inline styles but NOT observers or event listeners, so the displayed pages are a static
  // snapshot no paged.js machinery can ever mutate — the move/resize reflow cannot blank them.
  // The live originals stay in renderTarget (offscreen for the viewer; the print root for print,
  // which reveals renderTarget and ignores this return value). They render identically because
  // paged.js's page CSS is class-based + its size vars live on :root (both apply to clones).
  //
  // A progressive run returns the SAME clone nodes it already emitted (when the
  // hook saw every page), so the viewer's idempotent mount effect no-ops on the
  // already-mounted pages instead of remounting the whole report at the end. Any
  // mismatch (a hook miss) falls back to fresh post-run clones.
  pendingPageCallbacks.delete(content);
  const livePages = Array.from(renderTarget.querySelectorAll('.pagedjs_page'));
  const clones = pageEntry && pageEntry.clones.length === livePages.length
    ? pageEntry.clones
    : livePages.map((node) => node.cloneNode(true));
  // Temporary diagnostic (safe to keep): confirms the static-clone path ran and reports
  // per-page row counts, so a blank page can be told apart (0 rows = paged.js chunking gap;
  // >0 rows = a display problem) without guessing.
  try {
    const rowsPerPage = clones
      .map((c) => (c.querySelector('.pagedjs_page_content') || c).querySelectorAll('tr').length)
      .join('/');
    console.log(`[report] paginated ${clones.length} static page(s); rows/page: ${rowsPerPage}`); // eslint-disable-line no-console
  } catch (_) { /* diagnostic only */ }
  return clones;
}

export default paginateReport;
